import React, { useEffect } from "react";
import { Box, Typography, List, ListItem, ListItemText } from "@mui/material";
import { useLocation } from "react-router-dom";

const PrivacyPolicy = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0); // Scrolls to the top of the page
  }, [location.pathname]); // Triggers when the pathname changes

  return (
    <Box
      sx={{
        padding: "20px",
        maxWidth: "1100px",
        margin: "auto",
        marginTop: "60px",
      }}
    >
      <Typography variant="h4" gutterBottom>
        Privacy Policy
      </Typography>

      <Typography variant="subtitle1" gutterBottom>
        Last Updated: 14/07/2025
      </Typography>

      <Typography variant="body1" paragraph>
        This Privacy Policy outlines how UK TO MARS LTD collects, uses, stores,
        and protects your personal data in connection with our wholesale export
        and international transportation services. We are committed to
        safeguarding your privacy and ensuring compliance with UK data
        protection laws, including the UK General Data Protection Regulation (UK
        GDPR) and the Data Protection Act 2018.
      </Typography>

      {/* 1. Who We Are */}
      <Typography variant="h5" gutterBottom>
        1. Who We Are
      </Typography>
      <Typography variant="body1" paragraph>
        UK TO MARS LTD
        <br />
        Company Number: 15764998
        <br />
        Registered Office: 15 Little Balmer, Buckingham Industrial Estate,
        Buckingham, MK18 1TF
        <br />
        Email: info@uktomars.co.uk
        <br />
        Tel: +44 7564 627910
      </Typography>
      <Typography variant="body1" paragraph>
        We are a UK-based company specialising in wholesale export and
        international transportation of goods, primarily from the United Kingdom
        to the Middle East and other global destinations.
      </Typography>

      {/* 2. What Data We Collect */}
      <Typography variant="h5" gutterBottom>
        2. What Data We Collect
      </Typography>
      <Typography variant="body1" paragraph>
        We may collect the following information when you use our services or
        interact with our website:
      </Typography>
      <List>
        {[
          "• Name, company name, and contact details (email, phone, address)",
          "• Business registration numbers (if applicable)",
          "• Delivery and shipment addresses",
          "• Payment and invoicing details",
          "• ID or proof of address (if required for customs or compliance)",
          "• Correspondence history and service records",
          "• Website usage data (e.g. via cookies)",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 3. How We Use Your Data */}
      <Typography variant="h5" gutterBottom>
        3. How We Use Your Data
      </Typography>
      <Typography variant="body1" paragraph>
        We process your data for the following purposes:
      </Typography>
      <List>
        {[
          "• To manage export and transportation orders",
          "• To communicate with you about services or orders",
          "• To create shipping documentation, invoices, and customs declarations",
          "• To comply with legal and regulatory obligations (e.g. customs, VAT, export controls)",
          "• For customer support and dispute resolution",
          "• For fraud prevention and security",
          "• To improve our website and service delivery",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 4. Legal Basis for Processing */}
      <Typography variant="h5" gutterBottom>
        4. Legal Basis for Processing
      </Typography>
      <Typography variant="body1" paragraph>
        We rely on the following legal bases to process personal data:
      </Typography>
      <List>
        {[
          "• Contractual necessity: to fulfil our service agreements",
          "• Legal obligation: to meet customs/export compliance and tax requirements",
          "• Legitimate interest: for service improvement and fraud protection",
          "• Consent: where you opt in to marketing or optional features",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 5. Data Sharing */}
      <Typography variant="h5" gutterBottom>
        5. Data Sharing
      </Typography>
      <Typography variant="body1" paragraph>
        We only share your data when necessary, including with:
      </Typography>
      <List>
        {[
          "• Shipping carriers and freight partners",
          "• Customs authorities and compliance agents",
          "• Financial service providers (e.g. banks or accounting software)",
          "• IT and cloud service providers (e.g. email and document storage)",
          "• Legal or regulatory bodies as required by law",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>
      <Typography variant="body1" paragraph>
        We do not sell or trade your data with third parties.
      </Typography>

      {/* 6. International Transfers */}
      <Typography variant="h5" gutterBottom>
        6. International Transfers
      </Typography>
      <Typography variant="body1" paragraph>
        As part of our international trade activity, your data may be
        transferred to countries outside the UK. In such cases, we ensure
        appropriate safeguards are in place, such as standard contractual
        clauses or partner compliance with UK GDPR equivalents.
      </Typography>

      {/* 7. Data Retention */}
      <Typography variant="h5" gutterBottom>
        7. Data Retention
      </Typography>
      <Typography variant="body1" paragraph>
        We retain your data only as long as necessary to fulfil the purpose it
        was collected for, including:
      </Typography>
      <List>
        {[
          "• Legal record-keeping",
          "• Tax and customs documentation (typically 6 years)",
          "• Ongoing customer support",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 8. Your Rights */}
      <Typography variant="h5" gutterBottom>
        8. Your Rights
      </Typography>
      <Typography variant="body1" paragraph>
        You have the right to:
      </Typography>
      <List>
        {[
          "• Access the data we hold about you",
          "• Request correction or deletion of your data",
          "• Object to or restrict certain types of processing",
          "• Withdraw consent where applicable",
          "• Lodge a complaint with the Information Commissioner's Office (ICO)",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>
      <Typography variant="body1" paragraph>
        To exercise your rights, contact us at: info@uktomars.co.uk
      </Typography>

      {/* 9. Security Measures */}
      <Typography variant="h5" gutterBottom>
        9. Security Measures
      </Typography>
      <Typography variant="body1" paragraph>
        We use secure servers, encryption protocols, password protections, and
        access controls to safeguard your data. Only authorised personnel and
        partners have access to personal data necessary for service provision.
      </Typography>

      {/* 10. Cookies */}
      <Typography variant="h5" gutterBottom>
        10. Cookies
      </Typography>
      <Typography variant="body1" paragraph>
        Our website may use cookies to enhance user experience and monitor
        website performance. You can manage cookie preferences through your
        browser settings.
      </Typography>

      {/* 11. Updates to This Policy */}
      <Typography variant="h5" gutterBottom>
        11. Updates to This Policy
      </Typography>
      <Typography variant="body1" paragraph>
        We may update this Privacy Policy from time to time. The latest version
        will always be available at www.uktomars.co.uk and include the date of
        the latest revision.
      </Typography>
    </Box>
  );
};

export default PrivacyPolicy;
